import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Landmark, Plus, Trash2, X } from "lucide-react";

export default function Departments({ embedded }) {
  const qc = useQueryClient();
  const { data: boot } = useQuery({ queryKey: ["team-boot"], queryFn: async () => (await api.get("/team/bootstrap")).data });
  const { data: depts = [] } = useQuery({ queryKey: ["team-depts"], queryFn: async () => (await api.get("/team/departments")).data });
  const [edit, setEdit] = useState(null); // {id?, name, roles:[{id?,name}]}
  const locId = boot?.locations?.[0]?.id || "";

  const save = useMutation({
    mutationFn: async (d) => d.id
      ? (await api.put(`/team/departments/${d.id}`, { name: d.name, location_id: locId, roles: d.roles, order: d.order || 0 })).data
      : (await api.post("/team/departments", { name: d.name, location_id: locId, roles: d.roles, order: depts.length })).data,
    onSuccess: () => { toast.success("Αποθηκεύτηκε"); qc.invalidateQueries({ queryKey: ["team-depts"] }); setEdit(null); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/team/departments/${id}`)).data,
    onSuccess: () => { toast.success("Διαγράφηκε"); qc.invalidateQueries({ queryKey: ["team-depts"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });

  return (
    <div className="space-y-6" data-testid="departments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          {!embedded && <p className="font-mono text-xs uppercase tracking-widest text-[#8A8577]">Team &amp; Time</p>}
          <h1 className={`font-serif ${embedded ? "text-2xl" : "text-3xl md:text-4xl mt-1"} text-[#45423C] inline-flex items-center gap-2`}><Landmark className="h-6 w-6" /> Τμήματα &amp; Ρόλοι</h1>
        </div>
        <button data-testid="dept-add" onClick={() => setEdit({ name: "", roles: [] })}
          className="rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A] inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Νέο τμήμα</button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {depts.map((d) => (
          <div key={d.id} className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid={`dept-${d.id}`}>
            <div className="flex items-start justify-between">
              <h3 className="font-serif text-xl text-[#45423C]">{d.name}</h3>
              <button data-testid={`dept-del-${d.id}`} onClick={() => { if (window.confirm("Διαγραφή τμήματος;")) del.mutate(d.id); }} className="text-[#A1493A]"><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(d.roles || []).map((rl) => (
                <span key={rl.id} className="px-2 py-1 rounded-full bg-[#F1EDE6] text-xs text-[#6F6A60]">{rl.name}</span>
              ))}
            </div>
            <button data-testid={`dept-edit-${d.id}`} onClick={() => setEdit({ ...d })} className="mt-4 text-sm text-[#6E7A63] font-semibold">Επεξεργασία →</button>
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="dept-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl text-[#45423C]">{edit.id ? "Επεξεργασία" : "Νέο τμήμα"}</h2>
              <button onClick={() => setEdit(null)}><X className="h-5 w-5 text-[#6F6A60]" /></button>
            </div>
            <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Όνομα τμήματος</label>
            <input data-testid="dept-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })}
              className="mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2 text-sm mb-4" />
            <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Ρόλοι</label>
            <div className="mt-1 space-y-2">
              {(edit.roles || []).map((rl, i) => (
                <div key={i} className="flex gap-2">
                  <input data-testid={`dept-role-${i}`} value={rl.name} onChange={(e) => { const r = [...edit.roles]; r[i] = { ...r[i], name: e.target.value }; setEdit({ ...edit, roles: r }); }}
                    className="flex-1 rounded-xl border border-[#E3DED4] px-3 py-2 text-sm" />
                  <button onClick={() => setEdit({ ...edit, roles: edit.roles.filter((_, j) => j !== i) })} className="text-[#A1493A] px-2"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <button data-testid="dept-role-add" onClick={() => setEdit({ ...edit, roles: [...(edit.roles || []), { name: "" }] })}
                className="text-sm text-[#6E7A63] font-semibold inline-flex items-center gap-1"><Plus className="h-4 w-4" /> Προσθήκη ρόλου</button>
            </div>
            <div className="mt-6 flex gap-3">
              <button data-testid="dept-save" onClick={() => save.mutate({ ...edit, roles: (edit.roles || []).filter(r => r.name.trim()) })} disabled={!edit.name.trim() || save.isPending}
                className="flex-1 rounded-xl bg-[#45423C] text-white py-2.5 text-sm font-semibold disabled:opacity-60">Αποθήκευση</button>
              <button onClick={() => setEdit(null)} className="rounded-xl border border-[#E3DED4] px-4 py-2.5 text-sm">Άκυρο</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
